import { useCallback } from 'react';
import { graphql, useRelayEnvironment } from 'react-relay';
import commitMutation from 'relay-commit-mutation-promise';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { usePaymentMethodSelectContext } from '../PaymentMethodSelectContext';
import { guestWalletUpdater } from '../wallet/guestWalletUpdater';
import { type SubmitResult, createSubmitError } from '../submitPaymentMethod';
import { type PaymentMethod } from '../../types';

import { submitStripePaymentMethodCreateSetupIntentMutation } from './__generated__/submitStripePaymentMethodCreateSetupIntentMutation.graphql';
import { submitStripePaymentMethodSaveStripeMethodMutation } from './__generated__/submitStripePaymentMethodSaveStripeMethodMutation.graphql';

const createSetupIntentMutation = graphql`
    mutation submitStripePaymentMethodCreateSetupIntentMutation(
        $input: CreateStripeSetupIntentInput!
    ) {
        createStripeSetupIntent(input: $input) {
            setupIntent {
                clientSecret
            }
        }
    }
`;

const saveStripePaymentMethodMutation = graphql`
    mutation submitStripePaymentMethodSaveStripeMethodMutation(
        $input: SaveStripePaymentMethodInput!
    ) {
        saveStripePaymentMethod(input: $input) {
            user {
                wallet {
                    paymentMethods {
                        currencies
                        paymentMethod {
                            paymentMethodType
                            ... on CreditCard {
                                token
                                isExpired
                                cardHolderName
                                cardExpiryMonth
                                cardExpiryYear
                                maskedCardNumber
                                hashedCardNumber
                                cardType
                                cardLogo
                                formattedTextMaskedCardNumber
                                paymentProcessor
                            }
                            ... on PayPalAccount {
                                token
                                email
                                paymentProcessor
                            }
                            ... on ACH {
                                token
                                display
                                accountType
                                accountHolderName
                                last4
                                routingNumber
                                paymentProcessor
                                bankName
                            }
                            ... on ApplePay {
                                token
                                paymentProcessor
                            }
                        }
                        shippingAddresses {
                            serviceId
                        }
                    }
                }
                hasCreditCards
                hasBankAccounts
            }
            walletPaymentMethod {
                currencies
                paymentMethod {
                    paymentProcessor
                    __typename
                    ... on CreditCard {
                        token
                    }
                    ... on PayPalAccount {
                        token
                        email
                    }
                    ... on ApplePay {
                        token
                    }
                    ... on ACH {
                        token
                    }
                }
                shippingAddresses {
                    serviceId
                }
            }
        }
    }
`;

export function useStripeSubmit(): (paymentMethod: PaymentMethod) => Promise<SubmitResult> {
    const { userId, billingAddress, chargeCurrency, setIsBusy, mockWallet, viewerId } =
        usePaymentMethodSelectContext();
    const { email, country, zipCode, city, line1, line2, state } = billingAddress || {};

    const stripe = useStripe();
    const elements = useElements();
    const environment = useRelayEnvironment();

    return useCallback(
        async (paymentMethod: PaymentMethod) => {
            if (!stripe || !elements) {
                return createSubmitError(
                    'IMPLEMENTATION',
                    'Stripe Element not found, PaymentMethodSelectOptions must be a child of PaymentMethodSelect'
                );
            }

            if (!userId) {
                return createSubmitError('IMPLEMENTATION', 'userId is required to submit Stripe');
            }
            if (!email || !country || !zipCode) {
                return createSubmitError(
                    'VALIDATION',
                    '"email", "country" and "zipCode" need to be present on the billing address for Stripe submit'
                );
            }

            setIsBusy(true);

            const { error: stripeError } = await elements.submit();

            if (stripeError) {
                setIsBusy(false);
                return createSubmitError('VALIDATION', 'Stripe Elements error', { stripeError });
            }

            const setupIntentResponse =
                await commitMutation<submitStripePaymentMethodCreateSetupIntentMutation>(
                    environment,
                    {
                        mutation: createSetupIntentMutation,
                        variables: {
                            input: {
                                userId,
                                currency: chargeCurrency,
                            },
                        },
                    }
                );

            const clientSecret =
                setupIntentResponse?.createStripeSetupIntent?.setupIntent?.clientSecret || '';

            const stripeResponse = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    //we need to set this, but we won't redirect upon successful vaulting
                    return_url: 'https://www.1stdibs.com/',
                    payment_method_data: {
                        billing_details: {
                            email,
                            address: {
                                country,
                                postal_code: zipCode,
                                line1: line1 || '',
                                line2: line2 || '',
                                city: city || '',
                                state: state || '',
                            },
                        },
                    },
                },
                redirect: 'if_required',
            });
            const paymentMethodToken = stripeResponse?.setupIntent?.payment_method as string;

            if (stripeResponse.error || !paymentMethodToken) {
                setIsBusy(false);
                return createSubmitError('VAULT', stripeResponse.error?.message || 'Stripe error', {
                    stripeError: stripeResponse.error,
                });
            } else {
                await commitMutation<submitStripePaymentMethodSaveStripeMethodMutation>(
                    environment,
                    {
                        mutation: saveStripePaymentMethodMutation,
                        variables: {
                            input: {
                                userId,
                                paymentMethodToken,
                                currency: chargeCurrency,
                                mockWallet: !!mockWallet,
                            },
                        },
                        updater:
                            mockWallet && viewerId
                                ? store =>
                                      guestWalletUpdater<submitStripePaymentMethodSaveStripeMethodMutation>(
                                          store,
                                          viewerId,
                                          userId,
                                          'saveStripePaymentMethod'
                                      )
                                : null,
                    }
                );
            }

            setIsBusy(false);

            return {
                paymentMethod: {
                    ...paymentMethod,
                    paymentMethodToken,
                    paymentProcessor: 'STRIPE',
                },
            };
        },
        [
            stripe,
            elements,
            userId,
            email,
            country,
            zipCode,
            setIsBusy,
            environment,
            chargeCurrency,
            city,
            line1,
            line2,
            state,
            mockWallet,
            viewerId,
        ]
    );
}
